import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { MagazineViewer } from "@/components/magazine/MagazineViewer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import type { MagazineTitle, MagazineIssue, MagazinePage, MagazineObject, Article, MagazineTemplate } from "@shared/schema";

interface PageWithObjects extends MagazinePage {
  objects: (MagazineObject & { article?: Article | null })[];
  template?: MagazineTemplate | null;
  heroArticle?: Article | null;
  mainHeadlineArticle?: Article | null;
  secondaryHeadlineArticles?: Article[];
}

interface IssueData {
  title: MagazineTitle;
  issue: MagazineIssue;
  pages: PageWithObjects[];
}

export default function MagazineIssuePage() {
  const { slug, issueNumber } = useParams<{ slug: string; issueNumber: string }>();
  
  const { data, isLoading, error } = useQuery<IssueData>({
    queryKey: ["/api/magazines", slug, "issues", issueNumber],
  });

  if (isLoading) {
    return (
      <div className="h-screen flex flex-col bg-slate-900">
        <div className="px-4 py-3 bg-slate-800 border-b border-slate-700">
          <PageBreadcrumb
            items={[{ label: "Magazine", href: "/magazine" }, { label: "Loading…" }]}
            className="text-slate-400 [&_a]:text-slate-400 [&_a:hover]:text-white [&_.text-foreground]:text-white [&_.text-muted-foreground]:text-slate-400 mb-0"
          />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-400">Loading magazine...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="h-screen flex flex-col bg-slate-900">
        <div className="px-4 py-3 bg-slate-800 border-b border-slate-700">
          <PageBreadcrumb
            items={[{ label: "Magazine", href: "/magazine" }, { label: "Issue Not Found" }]}
            className="text-slate-400 [&_a]:text-slate-400 [&_a:hover]:text-white [&_.text-foreground]:text-white [&_.text-muted-foreground]:text-slate-400 mb-0"
          />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Issue Not Found</h2>
            <p className="text-slate-400 mb-4">
              This issue may not be published yet or doesn't exist.
            </p>
            <Link href="/magazine">
              <Button data-testid="button-back-to-magazines">
                Browse Magazines
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MagazineViewer 
      title={data.title}
      issue={data.issue}
      pages={data.pages}
    />
  );
}
